import React from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { AuthorizationResources } from "@rw1/permissions";

import {
  CompanyPage,
  ExperienceDetailsPage,
  ExperiencesPage,
  ExternalLicensesPage,
  GroupsPage,
  InsightsPage,
  UserDetailsPage,
  UsersPage,
} from "../pages";

import { UserGroupDetails } from "../pages/groups/GroupDetailsPage";
import { ExternalLicenseDetailsPage } from "../pages/licenses/ExternalLicenseDetailsPage";

import {
  Routes as AppRoutes,
  EExperienceTabs,
  EGroupTabs,
  EInsightsTabs,
  EUserTabs,
} from "../types/NavigationPaths";

import { useCurrentUser } from "../hooks";
import { useCurrentUserRoles } from "../hooks/useCurrentUserRoles";
import { Main } from "./Main";
import { ProtectedInsightsRoute } from "./ProtectedInsightsRoute";

const relative = (path: string) => path.replace(/^\/+/, "");

const getCompanyScopedRoutes = () => (
  <>
    <Route element={<CompanyPage />} index />

    <Route
      element={<CompanyPage />}
      path={relative(AppRoutes.COMPANY)}
    />

    <Route
      element={<UsersPage />}
      path={relative(AppRoutes.USERS)}
    />

    <Route path={`${relative(AppRoutes.USERS)}/:userId`}>
      <Route element={<Navigate replace to={EUserTabs.PROFILE} />} index />
      <Route element={<UserDetailsPage />} path=":tab" />
    </Route>

    <Route
      element={<GroupsPage />}
      path={relative(AppRoutes.GROUPS)}
    />

    <Route path={`${relative(AppRoutes.GROUPS)}/:groupId`}>
      <Route
        element={<Navigate replace to={EGroupTabs.OVERVIEW} />}
        index
      />
      <Route element={<UserGroupDetails />} path=":tab" />
    </Route>

    <Route
      element={<ExperiencesPage />}
      path={relative(AppRoutes.EXPERIENCES)}
    />

    <Route path={`${relative(AppRoutes.EXPERIENCES)}/:experienceId`}>
      <Route
        element={<Navigate replace to={EExperienceTabs.OVERVIEW} />}
        index
      />
      <Route element={<ExperienceDetailsPage />} path=":tab" />
    </Route>

    <Route
      element={<ExternalLicensesPage />}
      path={relative(AppRoutes.EXTERNAL_LICENSES)}
    />

    <Route path={`${relative(AppRoutes.EXTERNAL_LICENSES)}/:licenseId`}>
      <Route element={<ExternalLicenseDetailsPage />} index />
      <Route element={<ExternalLicenseDetailsPage />} path=":tab" />
    </Route>

    <Route path={relative(AppRoutes.INSIGHTS)}>
      <Route
        element={
          <ProtectedInsightsRoute>
            <Navigate replace to={EInsightsTabs.INTERNAL_USAGE} />
          </ProtectedInsightsRoute>
        }
        index
      />
      <Route
        element={
          <ProtectedInsightsRoute>
            <InsightsPage />
          </ProtectedInsightsRoute>
        }
        path=":tab"
      />
    </Route>
  </>
);

const CompanyRouteGuard: React.FC = () => {
  const { companyId } = useParams();
  const location = useLocation();
  const { user } = useCurrentUser();
  const { userRoles, isLoading: checkingAccess } = useCurrentUserRoles();

  const userCompanyId = user?.companyId;

  if (checkingAccess || (!userCompanyId && !userRoles?.length)) {
    return null;
  }

  const isGlobalAdmin = userRoles?.some(
    (x) => x.roleId === AuthorizationResources.Global.Group.Administrator
  );

  // Collect all distinct organization IDs the user has explicit roles for
  const accessibleOrgIds = Array.from(
    new Set(userRoles?.map((r) => r.organizationId).filter(Boolean) as string[])
  );

  // Check if the current URL's companyId is authorized for the user
  const hasAccessToCurrentCompany =
    isGlobalAdmin ||
    (companyId && accessibleOrgIds.includes(companyId)) ||
    companyId === userCompanyId;

  if (!hasAccessToCurrentCompany) {
    // Determine target company ID using priority rules:
    // 1. First role with a assigned non-user organization ID
    // 2. User's own companyId (for Global Admins or default)
    const targetCompanyId = accessibleOrgIds[0] ?? userCompanyId;

    const redirectedPath = location.pathname.replace(
      /^\/companies\/[^/]+/,
      `/companies/${targetCompanyId}`
    );

    return (
      <Navigate
        replace
        to={`${redirectedPath}${location.search}${location.hash}`}
      />
    );
  }

  return (
    <Main>
      <Outlet context={{ companyId: companyId ?? userCompanyId }} />
    </Main>
  );
};

const DefaultCompanyRedirect: React.FC = () => {
  const { user } = useCurrentUser();
  const { userRoles, isLoading } = useCurrentUserRoles();

  if (isLoading) {
    return null;
  }

  const firstAssignedOrgId = userRoles?.find((r) => r.organizationId)?.organizationId;
  const targetCompanyId = firstAssignedOrgId ?? user?.companyId;

  if (!targetCompanyId) {
    return null;
  }

  return <Navigate replace to={`/companies/${targetCompanyId}`} />;
};
export const MainLayout: React.FC = () => {
  return (
    <Routes>
      <Route element={<DefaultCompanyRedirect />} path="*" />
      <Route
        element={<CompanyRouteGuard />}
        path="companies/:companyId"
      >
        {getCompanyScopedRoutes()}
      </Route>
    </Routes>
  );
};